import { playIconWithBackground } from '../config/icons';
import { optimisedImage } from '../utils';
import { Box, Button, Stack, SvgIcon, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { ShadowButton } from './shadow-button';
import { LandingSectionTitle } from './landing-section-title';

export type GalleryThumbnailsProps = {
  images: {
    url: string;
    isVideo: boolean;
  }[];
  onClick?: () => void;
  t: (key: string) => string;
};

const Thumbnail = ({
  img,
  isLast,
  onClick,
  btnLabel,
  mediumImgSize,
}: {
  img: { url: string; isVideo: boolean };
  isLast?: boolean;
  onClick?: () => void;
  btnLabel?: string;
  mediumImgSize?: boolean;
}) => {
  // optimisedImage - needs sync draft functionality
  // const opImage = optimisedImage(img.url, mediumImgSize ? 'medium' : undefined);
  const opImage = img.url;
  return (
    <Box
      position="relative"
      onClick={onClick}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'opacity 0.3s',
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.8,
        },
      }}
    >
      {opImage && (
        <img
          src={opImage}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          alt="gallery image"
        />
      )}
      {img.isVideo && (
        <SvgIcon
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '30px', sm: '40px', md: '50px' },
            height: 'auto',
          }}
        >
          {playIconWithBackground}
        </SvgIcon>
      )}
      {isLast && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              '@media (max-width: 950px)': { display: 'none' },
            }}
          />
          <Button
            variant="contained"
            sx={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              '@media (max-width: 950px)': { display: 'none' },
            }}
          >
            <Typography variant="body1" color="primary">
              {btnLabel}
            </Typography>
          </Button>
        </>
      )}
    </Box>
  );
};

type GridProps = {
  children: React.ReactNode;
  halfWidthMobile?: boolean;
  halfHeightMobile?: boolean;
  halfWidthDesktop?: boolean;
  halfHeightDesktop?: boolean;
  hiddenMobile?: boolean;
};

const GridContainer = (props: GridProps) => {
  return (
    <Grid
      container
      spacing={2}
      size={{
        xs: props.halfWidthMobile ? 6 : 12,
        md: props.halfWidthDesktop ? 6 : 12,
      }}
      sx={{
        maxHeight: { xs: 'auto', md: '460px' },
        display: { xs: props.hiddenMobile ? 'none' : 'flex', md: 'flex' },
      }}
      height={{
        xs: props.halfHeightMobile ? 'calc(50% - 8px)' : 'auto',
        md: props.halfHeightDesktop ? 'calc(50% - 8px)' : 'auto',
      }}
    >
      {props.children}
    </Grid>
  );
};

const GridItem = (props: GridProps) => {
  return (
    <Grid
      size={{
        xs: props.halfWidthMobile ? 6 : 12,
        md: props.halfWidthDesktop ? 6 : 12,
      }}
      height={{
        xs: props.halfHeightMobile ? 'calc(50% - 8px)' : 'auto',
        md: props.halfHeightDesktop ? 'calc(50% - 8px)' : 'auto',
      }}
      sx={{
        maxHeight: { xs: '220px', md: '460px' },
        display: { xs: props.hiddenMobile ? 'none' : 'block', md: 'block' },
      }}
    >
      {props.children}
    </Grid>
  );
};

export function GalleryThumbnails({ images, onClick, t }: GalleryThumbnailsProps) {
  const hasOneImage = images.length === 1;
  const hasTwoImages = images.length === 2;
  const hasThreeImages = images.length === 3;
  const hasFourImages = images.length === 4;
  const hasFiveImages = images.length === 5;

  return (
    <Stack spacing={2} mx={{ xs: '16px', sm: '0' }}>
      <LandingSectionTitle title={t('labels.gallery')} />

      {hasOneImage && (
        <GridContainer>
          <GridItem>
            <Thumbnail img={images[0]} onClick={onClick} />
          </GridItem>
        </GridContainer>
      )}

      {hasTwoImages && (
        <GridContainer>
          <GridItem halfWidthMobile halfWidthDesktop>
            <Thumbnail img={images[0]} onClick={onClick} />
          </GridItem>
          <GridItem halfWidthMobile halfWidthDesktop>
            <Thumbnail
              img={images[1]}
              onClick={onClick}
              isLast
              btnLabel={t('labels.showAllPhotos')}
            />
          </GridItem>
        </GridContainer>
      )}

      {hasThreeImages && (
        <GridContainer>
          <GridItem halfWidthDesktop>
            <Thumbnail img={images[0]} onClick={onClick} />
          </GridItem>
          <GridContainer halfWidthDesktop>
            <GridItem halfHeightDesktop halfWidthMobile>
              <Thumbnail img={images[1]} onClick={onClick} />
            </GridItem>
            <GridItem halfHeightDesktop halfWidthMobile>
              <Thumbnail
                img={images[2]}
                onClick={onClick}
                isLast
                btnLabel={t('labels.showAllPhotos')}
              />
            </GridItem>
          </GridContainer>
        </GridContainer>
      )}

      {hasFourImages && (
        <GridContainer>
          <GridItem halfWidthDesktop>
            <Thumbnail img={images[0]} onClick={onClick} />
          </GridItem>
          <GridContainer halfWidthDesktop>
            {[images[1], images[2]].map((img, index) => (
              <GridItem halfHeightDesktop halfWidthDesktop halfWidthMobile key={index}>
                <Thumbnail img={img} onClick={onClick} mediumImgSize />
              </GridItem>
            ))}
            <GridItem halfHeightDesktop hiddenMobile>
              <Thumbnail
                isLast
                img={images[3]}
                onClick={onClick}
                btnLabel={t('labels.showAllPhotos')}
              />
            </GridItem>
          </GridContainer>
        </GridContainer>
      )}

      {hasFiveImages && (
        <GridContainer>
          <GridItem halfWidthDesktop>
            <Thumbnail img={images[0]} onClick={onClick} />
          </GridItem>
          <GridContainer halfWidthDesktop>
            {[images[1], images[2], images[3], images[4]].map((img, index) => (
              <GridItem
                key={index}
                halfWidthDesktop
                halfWidthMobile
                halfHeightDesktop
                hiddenMobile={index === 2 || index === 3}
              >
                <Thumbnail
                  img={img}
                  onClick={onClick}
                  isLast={index === 3}
                  btnLabel={t('labels.showAllPhotos')}
                  mediumImgSize
                />
              </GridItem>
            ))}
          </GridContainer>
        </GridContainer>
      )}

      <ShadowButton
        label={t('labels.showAllPhotos')}
        onClick={onClick}
        sx={{
          display: 'none',
          '@media (max-width: 950px)': {
            display: 'block',
          },
        }}
      />
    </Stack>
  );
}
